import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useConfig } from '../config/ConfigContext';
import apiClient from '../lib/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../components/Analytics';

const CartPage = () => {
  const { cart, dispatch } = useCart();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { config } = useConfig();

  // Helper function to get price for selected size
  const getPriceForSize = (product, size) => {
    if (!product?.details?.pricing_variants || !size) {
      return product?.price || 0;
    }
    const variant = product.details.pricing_variants.find(v => v.size === size);
    return variant ? variant.price : product.price;
  };

  useEffect(() => {
    trackEvent('page_view', { page_title: 'Cart' });
  }, []);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const productIds = [...new Set(cart.items.map(item => item.product_id))];
        const productPromises = productIds.map(id => apiClient.getProduct(id));
        const responses = await Promise.all(productPromises);

        const productsMap = {};
        responses.forEach(response => {
          productsMap[response.id] = response;
        });

        setProducts(productsMap);
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (cart.items.length > 0) {
      fetchProductDetails();
    } else {
      setLoading(false);
    }
  }, [cart.items]);

  const updateQuantity = (productId, size, quantity) => {
    if (quantity <= 0) {
      dispatch({ type: 'REMOVE_FROM_CART', payload: { product_id: productId, size } });
      trackEvent('remove_from_cart', { item_id: productId });
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { product_id: productId, size, quantity } });
    }
  };

  const removeItem = (productId, size) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { product_id: productId, size } });
    trackEvent('remove_from_cart', { item_id: productId });
  };

  const total = cart.items.reduce((sum, item) => {
    const product = products[item.product_id];
    if (!product) return sum;
    const itemPrice = getPriceForSize(product, item.size);
    return sum + (itemPrice * item.quantity);
  }, 0);

  if (loading || !config) {
    return <LoadingSpinner message="Loading your cart..." />;
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-4 md:pt-20 pb-12 bg-gray-50">
        <SEOHead title="Shopping Cart" noindex={true} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmptyState
            icon={
              <svg className="w-24 h-24 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            }
            title="Your Cart is Empty"
            description="Start building your dream bedroom with our premium collection"
            actionText="Start Shopping"
            actionLink="/products"
            actionClassName="inline-block bg-brand-blue hover:bg-brand-red text-white font-semibold px-8 py-3 rounded-none transition-all duration-300 shadow-lg"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 md:pt-20 pb-12 bg-gray-50">
      <SEOHead title="Shopping Cart" noindex={true} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Shopping Cart</h1>
          <p className="text-gray-600">Review your items before checkout</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md">
              {cart.items.map((item, index) => {
                const product = products[item.product_id];
                if (!product) return null;

                return (
                  <div key={`${item.product_id}-${item.size}-${index}`} className="p-4 sm:p-8 border-b last:border-b-0">
                    {/* Desktop Layout */}
                    <div className="hidden md:flex items-center space-x-6">
                      <img
                        src={(product.images && product.images[product.thumbnail_index || 0]) || '/api/placeholder/100/100'}
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded-xl shadow-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 mb-1">{product.name}</h3>
                        {item.size && <p className="text-sm text-gray-600 mb-2">Size: {item.size}</p>}
                        <p className="text-lg font-bold text-gray-900">${getPriceForSize(product, item.size).toFixed(2)}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold px-3 py-1 rounded-lg transition-colors"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.product_id, item.size, Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-16 text-center px-2 py-1 border border-gray-300 rounded-lg"
                        />
                        <button
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold px-3 py-1 rounded-lg transition-colors"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.product_id, item.size)}
                        className="text-red-500 hover:text-red-700 transition-colors p-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden">
                      <div className="flex gap-4 mb-4">
                        <img
                          src={(product.images && product.images[product.thumbnail_index || 0]) || '/api/placeholder/100/100'}
                          alt={product.name}
                          className="w-24 h-24 object-cover rounded-xl shadow-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-base text-gray-900 mb-1 line-clamp-2">{product.name}</h3>
                          {item.size && <p className="text-sm text-gray-600 mb-2">Size: {item.size}</p>}
                          <p className="text-xl font-bold text-gray-900">${getPriceForSize(product, item.size).toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                            className="bg-gray-200 active:bg-gray-300 text-gray-900 font-bold w-12 h-12 rounded-lg transition-colors flex items-center justify-center text-xl"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.product_id, item.size, Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-16 h-12 text-center px-2 border-2 border-gray-300 rounded-lg text-lg font-semibold"
                            readOnly
                          />
                          <button
                            onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                            className="bg-gray-200 active:bg-gray-300 text-gray-900 font-bold w-12 h-12 rounded-lg transition-colors flex items-center justify-center text-xl"
                          >
                            +
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.product_id, item.size)}
                          className="text-red-500 active:text-red-700 transition-colors p-3 bg-red-50 rounded-lg"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-8 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-lg">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span className="text-gray-600">Shipping:</span>
                  <span className="font-semibold text-green-600">FREE</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-xl">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold text-brand-blue">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  trackEvent('begin_checkout', {
                    currency: 'USD',
                    value: total,
                    items: cart.items.map(item => ({
                      item_id: item.product_id,
                      quantity: item.quantity
                    }))
                  });
                  navigate('/checkout');
                }}
                className="w-full bg-brand-blue hover:bg-brand-red text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 hover:shadow-lg"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;

